// src/pages/mock-data-management-dashboard/index.jsx
import React, { useState, useEffect } from 'react';

import HeaderBar from 'components/ui/HeaderBar';
import RoleBasedSidebar from 'components/ui/RoleBasedSidebar';
import BreadcrumbNavigation from 'components/ui/BreadcrumbNavigation';
import Icon from 'components/AppIcon';
import DataGenerationPanel from './components/DataGenerationPanel';
import MockDataPreview from './components/MockDataPreview';
import StatisticsSidebar from './components/StatisticsSidebar';
import ExportPanel from './components/ExportPanel';
import ApiEndpointSimulator from './components/ApiEndpointSimulator';
import BulkOperationsPanel from './components/BulkOperationsPanel';

const MockDataManagementDashboard = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('users');
  const [userRole] = useState('admin');
  const [mockData, setMockData] = useState({
    users: [],
    biodataRecords: [],
    documents: [],
    systemLogs: []
  });
  const [dataGenerationConfig, setDataGenerationConfig] = useState({
    users: { quantity: 10, randomization: true, pattern: 'realistic' },
    biodataRecords: { quantity: 50, randomization: true, pattern: 'realistic' },
    documents: { quantity: 25, randomization: true, pattern: 'realistic' },
    systemLogs: { quantity: 100, randomization: true, pattern: 'realistic' }
  });
  const [statistics, setStatistics] = useState({
    totalRecords: 0,
    apiCalls: 0,
    dataIntegrity: 100,
    lastGeneration: null
  });

  // Initialize mock data on component mount
  useEffect(() => {
    initializeMockData();
  }, []);

  const initializeMockData = () => {
    const initialUsers = generateUsers(10);
    const initialBiodata = generateBiodataRecords(50, initialUsers);
    const initialDocuments = generateDocuments(25, initialBiodata);
    const initialLogs = generateSystemLogs(100);

    setMockData({
      users: initialUsers,
      biodataRecords: initialBiodata,
      documents: initialDocuments,
      systemLogs: initialLogs
    });

    updateStatistics({
      totalRecords: initialUsers.length + initialBiodata.length + initialDocuments.length + initialLogs.length,
      apiCalls: 0,
      dataIntegrity: 100,
      lastGeneration: new Date()
    });
  };

  const generateUsers = (count) => {
    const roles = ['recruiter', 'clab', 'employer', 'admin'];
    const statuses = ['active', 'inactive', 'pending'];
    const users = [];

    for (let i = 0; i < count; i++) {
      users.push({
        id: `user_${i + 1}`,
        name: `User ${i + 1}`,
        email: `user${i + 1}@example.com`,
        role: roles[Math.floor(Math.random() * roles.length)],
        status: statuses[Math.floor(Math.random() * statuses.length)],
        createdAt: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000),
        lastLogin: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000),
        permissions: ['read', 'write'],
        profileComplete: Math.random() > 0.3
      });
    }
    return users;
  };

  const generateBiodataRecords = (count, users) => {
    const statuses = ['approved', 'pending', 'rejected', 'under_review'];
    const skills = ['Carpentry', 'Plumbing', 'Electrical', 'Masonry', 'Painting', 'Welding'];
    const records = [];

    for (let i = 0; i < count; i++) {
      const user = users[Math.floor(Math.random() * users.length)];
      records.push({
        id: `biodata_${i + 1}`,
        workerId: `WK-2024-${String(i + 1).padStart(3, '0')}`,
        name: `Worker ${i + 1}`,
        email: `worker${i + 1}@example.com`,
        phone: `+60${Math.floor(Math.random() * 100000000)}`,
        nationality: 'Malaysian',
        skills: skills.slice(0, Math.floor(Math.random() * 3) + 1),
        experience: Math.floor(Math.random() * 15) + 1,
        status: statuses[Math.floor(Math.random() * statuses.length)],
        submittedBy: user?.id,
        submittedAt: new Date(Date.now() - Math.random() * 60 * 24 * 60 * 60 * 1000),
        reviewedAt: Math.random() > 0.5 ? new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000) : null,
        documents: Math.floor(Math.random() * 5) + 1,
        completionScore: Math.floor(Math.random() * 30) + 70
      });
    }
    return records;
  };

  const generateDocuments = (count, biodataRecords) => {
    const documentTypes = ['passport', 'work_permit', 'certificate', 'medical_report', 'police_clearance'];
    const statuses = ['verified', 'pending', 'rejected', 'expired'];
    const documents = [];

    for (let i = 0; i < count; i++) {
      const biodata = biodataRecords[Math.floor(Math.random() * biodataRecords.length)];
      documents.push({
        id: `doc_${i + 1}`,
        biodataId: biodata?.id,
        workerId: biodata?.workerId,
        type: documentTypes[Math.floor(Math.random() * documentTypes.length)],
        filename: `document_${i + 1}.pdf`,
        size: Math.floor(Math.random() * 5000000) + 100000, // 100KB to 5MB
        status: statuses[Math.floor(Math.random() * statuses.length)],
        uploadedAt: new Date(Date.now() - Math.random() * 90 * 24 * 60 * 60 * 1000),
        verifiedAt: Math.random() > 0.4 ? new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000) : null,
        expiryDate: new Date(Date.now() + Math.random() * 365 * 24 * 60 * 60 * 1000),
        verifiedBy: Math.random() > 0.5 ? 'admin_user' : null
      });
    }
    return documents;
  };

  const generateSystemLogs = (count) => {
    const actions = ['CREATE', 'UPDATE', 'DELETE', 'LOGIN', 'LOGOUT', 'APPROVE', 'REJECT'];
    const entities = ['user', 'biodata', 'document', 'system'];
    const levels = ['INFO', 'WARN', 'ERROR', 'DEBUG'];
    const logs = [];

    for (let i = 0; i < count; i++) {
      logs.push({
        id: `log_${i + 1}`,
        timestamp: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000),
        level: levels[Math.floor(Math.random() * levels.length)],
        action: actions[Math.floor(Math.random() * actions.length)],
        entity: entities[Math.floor(Math.random() * entities.length)],
        entityId: `entity_${Math.floor(Math.random() * 1000)}`,
        userId: `user_${Math.floor(Math.random() * 50) + 1}`,
        message: `System ${actions[Math.floor(Math.random() * actions.length)].toLowerCase()} operation performed`,
        ipAddress: `192.168.1.${Math.floor(Math.random() * 255)}`,
        userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36'
      });
    }
    return logs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  };

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleDataGeneration = (entityType, config) => {
    let newData = [];
    const existingData = mockData[entityType] || [];
    
    switch (entityType) {
      case 'users':
        newData = generateUsers(config.quantity);
        break;
      case 'biodataRecords':
        newData = generateBiodataRecords(config.quantity, mockData.users);
        break;
      case 'documents':
        newData = generateDocuments(config.quantity, mockData.biodataRecords);
        break;
      case 'systemLogs':
        newData = generateSystemLogs(config.quantity);
        break;
      default:
        return;
    }

    const updatedMockData = {
      ...mockData,
      [entityType]: config.append ? [...existingData, ...newData] : newData
    };

    setMockData(updatedMockData);
    
    const totalRecords = Object.values(updatedMockData).reduce((sum, data) => sum + data.length, 0);
    updateStatistics({
      ...statistics,
      totalRecords,
      lastGeneration: new Date()
    });
  };

  const handleDataReset = (entityType) => {
    if (entityType === 'all') {
      setMockData({
        users: [],
        biodataRecords: [],
        documents: [],
        systemLogs: []
      });
      updateStatistics({
        totalRecords: 0,
        apiCalls: 0,
        dataIntegrity: 100,
        lastGeneration: null
      });
    } else {
      const updatedMockData = {
        ...mockData,
        [entityType]: []
      };
      setMockData(updatedMockData);
      
      const totalRecords = Object.values(updatedMockData).reduce((sum, data) => sum + data.length, 0);
      updateStatistics({
        ...statistics,
        totalRecords
      });
    }
  };

  const updateStatistics = (newStats) => {
    setStatistics(newStats);
  };

  const getTabConfig = () => {
    return [
      {
        id: 'users',
        label: 'Users',
        icon: 'Users',
        count: mockData.users.length,
        description: 'Manage user accounts and profiles'
      },
      {
        id: 'biodataRecords',
        label: 'Biodata Records',
        icon: 'FileText',
        count: mockData.biodataRecords.length,
        description: 'Worker biodata and employment records'
      },
      {
        id: 'documents',
        label: 'Documents',
        icon: 'FileImage',
        count: mockData.documents.length,
        description: 'Document attachments and verification'
      },
      {
        id: 'systemLogs',
        label: 'System Logs',
        icon: 'Activity',
        count: mockData.systemLogs.length,
        description: 'System activity and audit trail'
      }
    ];
  };

  return (
    <div className="min-h-screen bg-background">
      <HeaderBar 
        userRole={userRole}
        onSidebarToggle={handleSidebarToggle}
        userName="Mock Data Admin"
      />
      
      <RoleBasedSidebar 
        userRole={userRole}
        isCollapsed={sidebarCollapsed}
        onToggle={handleSidebarToggle}
      />

      <main className={`pt-16 transition-all duration-300 ease-smooth ${
        sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'
      }`}>
        <div className="flex">
          {/* Main Content */}
          <div className="flex-1 p-6">
            <div className="max-w-full">
              {/* Breadcrumb Navigation */}
              <BreadcrumbNavigation userRole={userRole} />

              {/* Page Header */}
              <div className="mb-8">
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="text-3xl font-bold text-text-primary mb-2">
                      Mock Data Management Dashboard
                    </h1>
                    <p className="text-text-secondary">
                      Generate, manage, and export test data for eBiodata CLAB platform development
                    </p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <ExportPanel 
                      activeTab={activeTab}
                      mockData={mockData}
                      onExport={() => updateStatistics({...statistics, apiCalls: statistics.apiCalls + 1})}
                    />
                    <BulkOperationsPanel 
                      onReset={handleDataReset}
                      onRefresh={initializeMockData}
                    />
                  </div>
                </div>
              </div>

              {/* Tabbed Navigation */}
              <div className="card mb-6">
                <div className="border-b border-secondary-200">
                  <nav className="flex space-x-8 px-6">
                    {getTabConfig().map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-150 flex items-center space-x-2 ${
                          activeTab === tab.id
                            ? 'border-primary text-primary' :'border-transparent text-text-secondary hover:text-text-primary hover:border-secondary-300'
                        }`}
                      >
                        <Icon name={tab.icon} size={16} />
                        <span>{tab.label}</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          activeTab === tab.id
                            ? 'bg-primary text-white' :'bg-secondary-100 text-secondary-600'
                        }`}>
                          {tab.count}
                        </span>
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Tab Content */}
                <div className="p-6">
                  <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
                    {/* Data Generation Panel */}
                    <div className="xl:col-span-1">
                      <DataGenerationPanel
                        activeTab={activeTab}
                        config={dataGenerationConfig[activeTab]}
                        onGenerate={handleDataGeneration}
                        onConfigChange={(config) => 
                          setDataGenerationConfig({
                            ...dataGenerationConfig,
                            [activeTab]: config
                          })
                        }
                      />
                    </div>

                    {/* Data Preview */}
                    <div className="xl:col-span-3">
                      <MockDataPreview
                        entityType={activeTab}
                        data={mockData[activeTab]}
                        onRefresh={() => handleDataGeneration(activeTab, dataGenerationConfig[activeTab])}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* API Endpoint Simulator */}
              <ApiEndpointSimulator 
                mockData={mockData}
                onApiCall={() => updateStatistics({...statistics, apiCalls: statistics.apiCalls + 1})}
              />
            </div>
          </div>

          {/* Statistics Sidebar */}
          <div className="hidden xl:block w-80">
            <StatisticsSidebar 
              statistics={statistics}
              mockData={mockData}
              onQuickAction={(action) => {
                if (action === 'resetAll') {
                  handleDataReset('all');
                } else if (action === 'generateSample') {
                  initializeMockData();
                }
              }}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default MockDataManagementDashboard;